import { get }                 from '@rails/request.js'
import BmiMixin                from '../../../../support/bmi_mixin'
import Vo2MaxMixin             from '../../../../support/vo2_max_mixin'
import WizardController        from '../../../wizard_controller'
import BloodPressureCheckMixin from '../../../../support/blood_pressure_check_mixin'
import mixin                   from '../../../../support/mixin'

export default class extends mixin(
  WizardController,
  BmiMixin,
  BloodPressureCheckMixin,
  Vo2MaxMixin
) {

  static targets = [
    ...super.targets,
    'chesterReadingsInput',
    'chesterStepReadings',
    'consent',
    'consentInput',
    'contraindicationsNoneOfTheAbove',
    'contraindicationsWarnings',
    'diastolicAtRest',
    'hivConfirmationTestResultInconclusive',
    'hivConfirmationTestResultReactive',
    'hivTestResult',
    'pulseAtRest',
    'pulseEscalation',
    'systolicAtRest',
    'testCompletion',
    'testCompletionInverse',
    'testEscalation',
    'testEscalationWarnings',
    'testPerform'
  ]

  static values = {
    ...super.values,
    rangeDataUrl: String
  }

  static outlets = [
    'consultation-note'
  ]

  connect() {
    super.connect()
    this.fetchEscalationRanges()
  }

  initialize() {
    super.initialize()
    this.contraindicationCategories = {}
    this.contraindicationMessages   = {}
    this.diastolicAtRestMax         = null
    this.elevatedBpMessage          = null
    this.elevatedPulseMessage       = null
    this.pulseAtRestMax             = null
    this.systolicAtRestMax          = null
  }

  // ==== Controllers

  // ==== Actions

  validReadingInputs() {
    return this.chesterReadingsInputTargets.every((input) => input.value !== '')
  }

  // === Escalation

  async fetchEscalationRanges() {
    const escalationRangeData = await this.getEscalationRanges()

    this.contraindicationCategories = escalationRangeData.contraindication_categories
    this.contraindicationMessages   = escalationRangeData.contraindication_messages
    this.diastolicAtRestMax         = escalationRangeData.diastolic_at_rest_max
    this.elevatedBpMessage          = escalationRangeData.elevated_bp_message
    this.elevatedPulseMessage       = escalationRangeData.elevated_pulse_message
    this.pulseAtRestMax             = escalationRangeData.pulse_at_rest_max
    this.systolicAtRestMax          = escalationRangeData.systolic_at_rest_max
  }

  async getEscalationRanges() {
    const response = await get(this.rangeDataUrlValue, { responseKind: 'json' })
    const body     = await response.json

    return body
  }

  // === Toggles

  toggleHivTestTaken(event) {
    if (event.currentTarget.value === 'patient_hiv_tested_today') {
      this.showRadioButtonMoreDetails({
        target: this.hivTestResultTarget
      })
    } else {
      this.hideRadioButtonMoreDetails({
        target: this.hivTestResultTarget
      })
    }
  }

  toggleHivTestResult(event) {
    [
      { value: 'reactive', target: this.hivConfirmationTestResultReactiveTarget },
      { value: 'inconclusive', target: this.hivConfirmationTestResultInconclusiveTarget }
    ].forEach((obj) => {
      if (event.currentTarget.value === obj.value) {
        this.showRadioButtonMoreDetails({
          target: obj.target
        })
      } else {
        this.hideRadioButtonMoreDetails({
          target: obj.target
        })
      }
    })
  }

  toggleContraindicationsNoneOfTheAbove({ currentTarget }) {
    this.toggleCheckboxNoneOfTheAbove({
      input:         currentTarget,
      targets:       this.contraindicationsNoneOfTheAboveTargets,
      dispatchInput: false
    })
    this.#toggleChesterStepReadings(
      this.contraindicationsNoneOfTheAboveTargets
    )
  }

  toggleTestEscalation({ currentTarget }) {
    if (currentTarget.value === '') return
    if (this.systolicAtRestValue === 0 || this.diastolicAtRestValue === 0) return

    const elevatedBpAtRest       = this.#isElevatedBP()
    const elevatedPulseAtRest    = this.#isElevatedPulse()
    const elevatedReadingsAtRest = (elevatedBpAtRest || elevatedPulseAtRest)

    if (elevatedReadingsAtRest) this.warningMessage = this.#i18nMessage(elevatedBpAtRest, elevatedPulseAtRest)

    this.toggleTarget(this.pulseEscalationTarget, !elevatedBpAtRest)
    this.toggleTarget(this.consentTarget, !elevatedReadingsAtRest)
    this.toggleTarget(this.testPerformTarget, this.consentInputTarget.checked && !elevatedReadingsAtRest)
    this.toggleTarget(this.testEscalationWarningsTarget, elevatedReadingsAtRest)
  }

  toggleTestPerform({ currentTarget: { checked } }) {
    this.toggleTarget(this.testPerformTarget, checked)
  }

  toggleTestCompletion({ currentTarget: { checked } }) {
    this.toggleTarget(this.testCompletionInverseTarget, checked)
    this.toggleTarget(this.testCompletionTarget, !checked)
  }

  // ==== Getters

  get systolicAtRestValue() {
    const value = parseInt(this.systolicAtRestTarget.value, 10)
    return Number.isNaN(value) ? 0 : value
  }

  get diastolicAtRestValue() {
    const value = parseInt(this.diastolicAtRestTarget.value, 10)
    return Number.isNaN(value) ? 0 : value
  }

  get pulseAtRestValue() {
    const value = parseInt(this.pulseAtRestTarget.value, 10)
    return Number.isNaN(value) ? 0 : value
  }

  // ==== Setters

  /**
   * @param {any} message
   */
  set warningMessage(message) {
    this.testEscalationWarningsTarget.querySelector('p').innerHTML = message
  }

  // === Private

  #toggleChesterStepReadings(...targetGroups) {
    const allNoneChecked = targetGroups.every((targets) => {
      const noneCheckbox = targets.find((target) => target.value === 'none_of_the_above')
      if (!noneCheckbox) throw new Error(`missing none_of_the_above checkbox: ${targets[0]?.name || 'unknown'}`)
      return noneCheckbox.checked || targets.every((target) => !target.checked)
    })

    this.chesterStepReadingsTarget.classList.toggle(this.hideClass, !allNoneChecked)
    this.contraindicationsWarningsTarget.classList.toggle(this.hideClass, allNoneChecked)
    this.#updateContraindicationsWarningMessage()
  }

  #updateContraindicationsWarningMessage() {
    const categories = this.contraindicationsNoneOfTheAboveTargets
      .filter((target) => target.checked)
      .map((target) => this.contraindicationCategories[target.value])

    const category = Object.keys(this.contraindicationMessages).find((key) => categories.includes(key))
    if (!category) return

    this.contraindicationsWarningsTarget.querySelector('p').innerHTML = this.contraindicationMessages[category]
  }

  #isElevatedBP() {
    return this.systolicAtRestValue > this.systolicAtRestMax || this.diastolicAtRestValue > this.diastolicAtRestMax
  }

  #isElevatedPulse() {
    return this.pulseAtRestValue > this.pulseAtRestMax
  }

  #i18nMessage(elevatedBpAtRest, elevatedPulse) {
    if (elevatedPulse && !elevatedBpAtRest) return this.elevatedPulseMessage

    return this.elevatedBpMessage
  }

}
